import { Box } from '@mui/material'
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import fiLocale from 'date-fns/locale/fi'
import type { FC } from 'react'

/**
 *
 * @param onChange
 * @param value
 * @param dateToCompare date value to compare against
 * @param dateToCompareFieldName field name of the compared date value
 * @param isStartDate indicates if the value is startDate
 * @param label
 * @param name
 * @param readOnly indicates if component should be in read only state
 * @param isRequired
 * @param isError
 * @param children
 * @returns Custom date picker with functionality to compare against another date
 */
const CustomDateTimePicker: FC<{
  onChange: (date: Date | null, name: string, keyboardInputValue?: string | undefined) => void
  value: Date | null
  dateToCompare?: Date | null
  dateToCompareFieldName?: string
  isStartDate?: boolean
  label: string
  name: string
  readOnly?: boolean
  isRequired?: boolean
  isError?: boolean
  helperText?: string
  minDateTime?: Date
  children?: React.ReactNode
}> = ({
  onChange,
  value,
  dateToCompare,
  dateToCompareFieldName = '',
  isStartDate,
  label,
  name,
  readOnly = false,
  isRequired = false,
  isError = false,
  helperText,
  minDateTime,
  children
}) => {
  const compareDates = (date: Date, dateToCompare: Date): void => {
    if (isStartDate && dateToCompare < date) {
      onChange(date, dateToCompareFieldName)
    }

    if (!isStartDate && dateToCompare > date) {
      onChange(date, dateToCompareFieldName)
    }
  }

  const handleChange = (date: Date | null) => {
    onChange(date, name)
  }

  const handleAccept = (date: Date | null) => {
    // only compare date values, if dateToCompare is provided in props
    if (dateToCompare && date !== null) {
      compareDates(date, dateToCompare)
    }
  }

  return (
    <Box sx={{ position: 'relative', width: 260 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fiLocale}>
        <DesktopDateTimePicker
          format={'dd/MM/yyyy HH:mm'}
          ampm={false}
          value={value}
          onChange={handleChange}
          onAccept={handleAccept}
          label={label}
          readOnly={readOnly}
          minDateTime={minDateTime}
          timeSteps={{ minutes: 1, seconds: 1 }}
          views={['day', 'hours', 'minutes']}
          slotProps={{
            textField: {
              error: isError,
              required: isRequired,
              fullWidth: true,
              helperText: helperText
            },
            actionBar: {
              actions: ['today', 'accept']
            }
          }}
        />
        {children}
      </LocalizationProvider>
    </Box>
  )
}
export default CustomDateTimePicker
